import type { ReactNode } from "react";
import type { FeedbackItem } from "../../../api";
import { formatDateTime } from "../../../utils";
import { FeedbackStatusBadge } from "./FeedbackStatusBadge";
import { previewText } from "./feedbackUtils";
import { LoadingText } from "../../ui";

type FeedbackListProps = {
  items: FeedbackItem[];
  loading: boolean;
  selectedId: string | null;
  onSelect: (id: string) => void;
};

export function FeedbackList({
  items,
  loading,
  selectedId,
  onSelect,
}: FeedbackListProps) {
  if (loading) {
    return (
      <Panel>
        <LoadingText className="p-4">Loading feedback...</LoadingText>
      </Panel>
    );
  }

  if (items.length === 0) {
    return (
      <Panel>
        <p className="p-4 text-sm text-gray-400">No feedback found.</p>
      </Panel>
    );
  }

  return (
    <Panel>
      <div className="divide-y divide-gray-100">
        {items.map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => onSelect(item.id)}
            className={`block w-full px-4 py-3 text-left hover:bg-gray-50 ${
              selectedId === item.id ? "bg-blue-50" : "bg-white"
            }`}
          >
            <div className="mb-2 flex items-center justify-between gap-2">
              <span className="truncate text-sm font-medium text-gray-900">
                {item.reporter?.full_name ?? "Unknown user"}
              </span>
              <FeedbackStatusBadge status={item.status} />
            </div>
            <p className="line-clamp-2 text-sm text-gray-600">
              {previewText(item.comment)}
            </p>
            <p className="mt-2 text-xs text-gray-400">
              {formatDateTime(item.created_at)}
            </p>
          </button>
        ))}
      </div>
    </Panel>
  );
}

function Panel({ children }: { children: ReactNode }) {
  return (
    <div className="min-h-[260px] rounded-lg border border-gray-200 bg-white">
      {children}
    </div>
  );
}
